import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  TextField,
  Grid,
  useMediaQuery,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@material-ui/core';
import './LoginPage.css';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  isLoggedIn,
  btnColor,
  mainBlueColor,
  userPlanId,
  userMenuId,
  userSiteManagePlanId,
  isEContractServiceEnabledState
} from '../GlobalState';
import logo from '../assets/logo1.png';
import { getUserPermission } from '../apicalls/employeeMaster';
import LoadingComponent from '../components/LoadingComponent';
import { ROLE, PLAN } from '../const/Const';
import { DASHBOARD } from '../components/topicPath/const/MenuTree';

export const LoginPage: React.FC = () => {
  const [buttonColor] = useRecoilState(btnColor);
  const [mainColor] = useRecoilState(mainBlueColor);
  const [userPlan, setUserPlan] = useRecoilState<any>(userPlanId);
  const [userMenu, setUserMenu] = useRecoilState<any>(userMenuId);
  const setSiteManagePlan = useSetRecoilState<any>(userSiteManagePlanId);
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      main: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: window.innerHeight,
        backgroundColor: '#52555F'
      },
      root: {
        '& .MuiTextField-root': {
          margin: theme.spacing(1),
          width: '25ch'
        }
      },
      input: {
        width: '100%',
        backgroundColor: '#FFFFFF'
      },
      btnContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    })
  );

  const classes = useStyles();
  const matches2 = useMediaQuery('(min-width:900px)');
  const history = useHistory();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [loggedIn, setLoggedIn] = useRecoilState(isLoggedIn);
  const [userId, setUserId] = useState();
  const [mfaEnabled, setMfaEnabled] = useState();
  const [loginButtonDisabled, setLoginButtonDisabled] = useState(false);
  const changeLoggedInData = (newData: string) => setLoggedIn(newData);
  // ログイン状態
  const [isLogin, setIsLogin] = useState<boolean>();

  useEffect(() => {
    isLoginCheck();
  }, []);

  /**
   * ログインチェック
   */
  const isLoginCheck = useCallback(() => {
    // バックエンドのソースには「get_user_roles」と記載のあるエンドポイントへアクセス
    getUserPermission().then(data => {
      // 未ログイン状態の場合
      if (data === 'Not logged in!') {
        setIsLogin(false);
      } else {
        // エラーの場合
        if (typeof data === 'string') {
          setIsLogin(false);
          setErrorMessage(data);
        } else {
          // エラーの場合
          if (data.error) {
            setIsLogin(false);
            setErrorMessage(data.error);

            // ログイン状態の場合
          } else {
            // アクセス可能なメニューリストにダッシュボードがあればダッシュボードに遷移。なければ工事
            const tree: string[] = data.accessible_menu_tree;
            if (tree.includes(DASHBOARD.menuId)) {
              history.replace('/dashboard');
            } else {
              history.replace('/constructions', data.user_id);
            }
          }
        }
      }
    });
  }, []);

  const loginPressed = () => {
    let val =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (email.trim() === '') {
      setEmailError('メールアドレスを入力してください。');
      setErrorMessage('');
      setPasswordError('');
    } else if (!val.test(email.trim())) {
      setEmailError('メールアドレスの形式が不正です。');
      setErrorMessage('');
      setPasswordError('');
    } else if (password.trim() === '') {
      setPasswordError('パスワードを入力してください。');
      setEmailError('');
      setErrorMessage('');
    } else {
      // ログインボタンを押せないようにする。連続クリック防止。
      setLoginButtonDisabled(true);
      axios
        .post(
          `/user/login`,
          {
            email: email,
            password: password
          },
          {
            headers: {
              withCredentials: true
            }
          }
        )
        .then(function (response) {
          setLoginButtonDisabled(false);
          if (response.data.message) {
            changeLoggedInData('');
            setUserPlan(response.data.plan_id);
            setUserMenu(response.data.menu);
            setMfaEnabled(response.data.mfa_enabled);
            setSiteManagePlan(response.data.site_manage_plan_id);
            const tree = response.data.accessible_menu_tree;
            // 2段階認証が有効の場合は、確認コードを入力する画面へ遷移
            if (response.data.mfa_enabled) {
              const user = {
                id: response.data.user_id,
                email,
                role: response.data.role,
                planId: response.data.plan_id,
                menu: response.data.menu
              };
              history.replace('/mfa', user);
              // アクセス可能なメニューリストにダッシュボードがあればダッシュボードに遷移。なければ工事
            } else if (tree.includes(DASHBOARD.menuId)) {
              history.replace('/dashboard');
            } else {
              history.replace('/constructions', response.data.user_id);
            }
          } else if (response.data.error) {
            setErrorMessage(response.data.error);
            setEmailError('');
            setPasswordError('');
          } else {
            setErrorMessage('問題が発生しました。もう一度実行してください。');
            setEmailError('');
            setPasswordError('');
          }
          if (response.data.message) {
            // CONOC用とテスト用のアカウントは除外する。以下は該当のテナントidであり、増えた場合は以下に追加する。
            const exclusion_tenant_ids = [
              1, 2, 3, 28, 34, 43, 150, 164, 393, 394, 424
            ];
            const tenantId = response.data.tenant_id;
            if (exclusion_tenant_ids.includes(tenantId)) {
              document.cookie =
                'traffic_type=internal; path=/; max-age=15768000'; //cookieは半年間有効にする
            }
          }
        })
        .catch(err => {
          setLoginButtonDisabled(false);
          if (err.response.data) {
            if (err.response.data.error) {
              setEmailError('');
              setPasswordError('');
              setErrorMessage(err.response.data.error);
            } else {
              setEmailError('');
              setPasswordError('');
              setErrorMessage('問題が発生しました。もう一度実行してください。');
            }
          } else {
            setEmailError('');
            setPasswordError('');
            setErrorMessage('問題が発生しました。もう一度実行してください。');
          }
        });
    }
  };

  return (
    <>
      {isLogin == undefined ? (
        <>
          <LoadingComponent />
        </>
      ) : (
        <div className={classes.main}>
          <Grid item xs={matches2 ? 6 : 12}>
            <div className='container'>
              <div className={classes.btnContainer}>
                <img
                  style={{ width: 100, height: 60, objectFit: 'contain' }}
                  alt='logo'
                  src={logo}
                />
              </div>
              <div>
                <TextField
                  className={classes.input}
                  id='outlined-mail'
                  label='メールアドレス'
                  variant='outlined'
                  onChange={text => {
                    setEmail(text.target.value);
                  }}
                  inputProps={{
                    maxLength: 50
                  }}
                />
              </div>
              <div style={{ color: '#DE4839' }}>{emailError}</div>
              <div style={{ marginTop: 30 }}>
                <TextField
                  className={classes.input}
                  id='outlined-password-input'
                  label='パスワード'
                  type='password'
                  autoComplete='current-password'
                  variant='outlined'
                  onChange={text => {
                    setPassword(text.target.value);
                  }}
                />
              </div>
              <div style={{ color: '#DE4839' }}>{passwordError}</div>
              <div style={{ marginTop: 10 }} className={classes.btnContainer}>
                <div style={{ color: '#DE4839' }}>{errorMessage}</div>
              </div>
              <div className={classes.btnContainer}>
                <Button
                  onClick={() => {
                    loginPressed();
                  }}
                  style={{
                    backgroundColor: loginButtonDisabled ? 'gray' : buttonColor,
                    marginTop: 30
                  }}
                  variant='contained'
                  color='primary'
                  disabled={loginButtonDisabled}
                >
                  {loginButtonDisabled ? (
                    <CircularProgress size={25} />
                  ) : (
                    'ログイン'
                  )}
                </Button>
              </div>
              <div
                style={{ marginTop: 20, cursor: 'pointer' }}
                className={classes.btnContainer}
                onClick={() => {
                  history.push('/reset');
                }}
              >
                <div
                  style={{ textDecorationLine: 'underline', color: mainColor }}
                >
                  パスワードを忘れた方はこちら。
                </div>
              </div>
            </div>
          </Grid>
        </div>
      )}
    </>
  );
};
